import { ApolloError, useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { showSnackbar } from "@/lib/snackbarUtils";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import DetailsCard from "@/components/DetailsCard";
import SegmentedButton from "@/components/SegmentedButton";
import Autocomplete from "@/components/Autocomplete";
import TextField from "@/components/TextField";
import { PAT_EDIT_SUBMISSION_MUTATION } from "../queries";
import { EventRegistrationAdditionalStatus, RegistrantRow } from "../types";
import { patSubmissionStatusOptions } from "../_utils";
import { toDateFormat } from "@/lib/dateHelpers";
import DateSelect from "@/components/DateSelect";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import FileUpload from "@/components/FileUpload";
import { isExcessiveScore } from "../_utils";
import { useOutletContext, useParams } from "react-router-dom";
import type { EventOutletContext } from "../../_layout";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

const EXCESSIVE_SCORE = "excessive_score";

export type EditPatScoreSubmission = Omit<
  Pick<
    RegistrantRow,
    | "id"
    | "attended"
    | "note"
    | "score_18_1"
    | "score_18_2"
    | "score_target"
    | "score_total"
    | "additionalStatus"
    | "date"
    | "suspension"
  >,
  "suspension"
> & {
  suspension: {
    startDate: string;
    endDate: string | null;
  } | null;
};

interface Props {
  onClose: () => void;
  submission: EditPatScoreSubmission;
}

interface Input {
  id: string;
  attended: boolean;
  roundOneScore: number;
  roundTwoScore: number;
  additionalStatus: string;
  note: string | null;
  suspensionEndDate?: string | null;
  document?: {
    name: string;
    file: File;
  };
}

function toBoolean(value: string) {
  return value === "true";
}

function cond(pairs: Array<[boolean, string]>): string {
  const pair = pairs.find(([condition]) => condition);
  return pair ? pair[1] : "";
}

export default function EditScoreModal({ onClose, submission }: Props) {
  const { Modal } = useModal();
  const [mutate] = useMutation(PAT_EDIT_SUBMISSION_MUTATION);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadErrorMsg, setFileUploadErrorMsg] = useState("");
  const outletContext = useOutletContext<EventOutletContext | undefined>();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const defaultNinetyDayEndDate = toDateFormat(DateTime.fromISO(submission.date).plus({ days: 90 }).toISO() || "", {
    format: "yyyy-MM-dd"
  });
  const temporalSuspensionEndDate = isExcessiveScore(Number(submission.score_total))
    ? submission.suspension?.endDate || defaultNinetyDayEndDate
    : submission.suspension?.startDate
      ? submission.suspension.endDate
      : defaultNinetyDayEndDate;

  const defaultValues = {
    attended: (!!submission.attended).toString(),
    note: submission.note,
    round1Score: submission.score_18_1,
    round2Score: submission.score_18_2,
    status: isExcessiveScore(Number(submission.score_total)) ? EXCESSIVE_SCORE : submission.additionalStatus,
    suspStartDate: {
      startDate: submission.suspension?.startDate || null,
      endDate: submission.suspension?.startDate || null
    },
    suspEndDate: {
      startDate: temporalSuspensionEndDate,
      endDate: temporalSuspensionEndDate
    }
  };
  type FieldValues = typeof defaultValues;

  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    watch
  } = useForm({
    defaultValues,
    mode: "onBlur"
  });
  const [statusQuery, setStatusQuery] = useState(
    () => patSubmissionStatusOptions().find((option) => option.value === defaultValues.status)?.displayText ?? ""
  );

  const roundScoreNumber = {
    validate: (value: string) =>
      !toBoolean(getValues("attended")) ||
      watch("status") === EventRegistrationAdditionalStatus.EXCUSED ||
      /^\d+$/.test(value)
  };

  const requiredIfStatus = {
    validate: (value: string, values: FieldValues) => {
      if (isExcessiveScore(Number(values.round1Score) + Number(values.round2Score))) return true;
      return !(!value && !!values["status"]);
    }
  };

  const attended = toBoolean(watch("attended"));
  const totalScore = watch(["round1Score", "round2Score"]).reduce((acc, x) => acc + +x, 0);
  const isExcessive = isExcessiveScore(totalScore);
  const isExcused = watch("status") === EventRegistrationAdditionalStatus.EXCUSED;
  const isSuspended = !!watch("status") && !isExcused;
  const passingScore = totalScore <= (submission.score_target || 0);

  const calculateQualifyingScore = (targetScore: number | null) => {
    if (!targetScore) return null;
    return Math.floor(targetScore / 2) + 5;
  };

  const qualifyingScore = calculateQualifyingScore(submission.score_target);

  const round1Score = +watch("round1Score") || 0;
  const round2Score = +watch("round2Score") || 0;
  const isQualifying =
    qualifyingScore !== null && !passingScore && (round1Score <= qualifyingScore || round2Score <= qualifyingScore);

  const resultHeadline = cond([
    [isExcused, "Excused"],
    [!attended, "Absent"],
    [isExcessive, "Excessive Score"],
    [isSuspended, "Suspended"],
    [passingScore, "Pass"],
    [isQualifying, "Qualifying"],
    [!passingScore && !isQualifying, "Fail"]
  ]);

  const suspStartDate = toDateFormat(submission.date);
  const _endDate = watch("suspEndDate").endDate;
  const suspEndDate = _endDate ? toDateFormat(_endDate) : "Indefinite";

  const supportText = cond([
    [isExcused, ""],
    [!attended, ""],
    [isExcessive || isSuspended, `${suspStartDate} - ${suspEndDate}`],
    [
      true,
      `Total: ${totalScore}
         Target: ${submission.score_target}`
    ]
  ]);

  useEffect(() => {
    if (isExcessive) {
      const hadStatus = !!watch("status");
      if (hadStatus) {
        setValue("status", "", { shouldValidate: true });
        setStatusQuery("");
      }
      setValue("note", "", { shouldValidate: true });
      clearErrors("note");
      if (hadStatus) void trigger();
      return;
    }
    if (watch("status") == EXCESSIVE_SCORE) {
      setValue("status", "");
      setStatusQuery("");
    }
  }, [clearErrors, isExcessive, setValue, trigger, watch]);

  const submit = handleSubmit(async (fields: FieldValues) => {
    try {
      const nullableNote = isExcessive ? submission.note || null : !!fields.status ? fields.note : null;
      const status = isExcessive ? "" : fields.status;
      const input: Input = {
        id: submission.id,
        attended: toBoolean(fields.attended),
        roundOneScore: +fields.round1Score,
        roundTwoScore: +fields.round2Score,
        additionalStatus: status,
        note: nullableNote,
        ...(selectedFile && {
          document: {
            name: selectedFile.name,
            file: selectedFile
          }
        })
      };

      if ((isExcessive || isSuspended) && !isExcused) input.suspensionEndDate = fields.suspEndDate.startDate;

      const response = await mutate({
        variables: { input: input }
      });
      const { message, success } = response.data?.patEditSubmission;

      if (success) {
        showSnackbar("Submission edited successfully");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.scoresUpdated,
          getEventAnalyticsData({
            source: "results",
            eventId: outletContext?.eventData?.id,
            eventSlug,
            registrationId: submission.id,
            action: "edit"
          })
        );
      } else {
        showSnackbar(`Unable to edit submission: ${message}`);
      }
    } catch (error) {
      showSnackbar(`Unable to edit submission: ${(error as ApolloError).message}`);
      setFileUploadErrorMsg("Error uploading document");
    } finally {
      onClose();
    }
  });

  const action = (
    <Button disabled={!isValid} onClick={submit}>
      Re-Finalize Score
    </Button>
  );

  return (
    <Modal
      onClosed={onClose}
      headline="Edit PAT Score"
      data-testid="editscore-modal"
      action={action}
      className="max-h-[85vh]"
    >
      <div className="flex flex-col gap-6">
        <p>
          <span className="font-bold">Note:</span> If the result of this update also changes the overall PAT status for
          this person, they will be sent an email informing them of the new status.
        </p>
        <div className="w-fit">
          <Controller
            control={control}
            name="attended"
            render={({ field: { value } }) => (
              <SegmentedButton
                ariaLabel="Attendance"
                showCheckmark
                options={[
                  { label: "Present", value: "true" },
                  { label: "Absent", value: "false" }
                ]}
                value={value}
                onChange={(selection) => {
                  selection && setValue("attended", selection);
                  setValue("round1Score", getValues("round1Score"), { shouldValidate: true });
                  setValue("round2Score", getValues("round2Score"), { shouldValidate: true });
                }}
              />
            )}
          />
        </div>
        {attended && (
          <div className="flex gap-4">
            <Controller
              control={control}
              name="round1Score"
              rules={roundScoreNumber}
              render={({ field: { onChange, value } }) => (
                <TextField
                  className="flex-1"
                  label="1st 18 Hole Score"
                  value={value}
                  onChange={onChange}
                  error={!!errors.round1Score}
                  errorText={errors.round1Score?.message}
                  required={attended}
                />
              )}
            />
            <Controller
              control={control}
              name="round2Score"
              rules={roundScoreNumber}
              render={({ field: { onChange, value } }) => (
                <TextField
                  className="flex-1"
                  label="2nd 18 Hole Score"
                  value={value}
                  onChange={onChange}
                  error={!!errors.round2Score}
                  errorText={errors.round2Score?.message}
                  required={attended}
                />
              )}
            />
          </div>
        )}
        <DetailsCard
          compact
          options={[
            {
              headline: resultHeadline,
              iconName: "golf_course",
              overline: "PAT Result",
              supportingText: supportText
            }
          ]}
        />
        {!isExcessive && (
          <Controller
            control={control}
            name="status"
            render={({ field: { onChange, value } }) => {
              const options = patSubmissionStatusOptions();
              const selectedLabel = options.find((option) => option.value === value)?.displayText ?? "";
              const data =
                !statusQuery || statusQuery === selectedLabel
                  ? options
                  : options.filter((option) => option.displayText.toLowerCase().includes(statusQuery.toLowerCase()));

              return (
                <Autocomplete
                  variant="searchableDropdown"
                  label="Alternate Non-Scoring Result"
                  query={statusQuery}
                  data={data}
                  onChangeText={setStatusQuery}
                  reset={() => {
                    onChange("");
                    setStatusQuery("");
                  }}
                  onSelect={(option) => {
                    onChange(option.value);
                    setStatusQuery(option.displayText);
                  }}
                  renderItem={(option) => <span>{option.displayText}</span>}
                  error={!!errors.status}
                  errorText={errors.status?.message as string}
                  constrainMenuWidth
                  menuClassName="max-h-[300px]"
                />
              );
            }}
          />
        )}
        {watch("status") && !isExcessive && (
          <Controller
            control={control}
            name="note"
            rules={requiredIfStatus}
            shouldUnregister
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Details"
                rows={2}
                className="w-full"
                type="textarea"
                onChange={onChange}
                value={value || ""}
                required={true}
              />
            )}
          />
        )}
        {(watch("status") || isExcessive) && !isExcused && (
          <div className="flex gap-4">
            <DateSelect
              disabled
              label="Suspension Start Date"
              useModal={true}
              onChange={() => null}
              useSingleDate
              value={{ startDate: submission.date, endDate: submission.date }}
            />
            <Controller
              control={control}
              name="suspEndDate"
              render={({ field: { onChange, value } }) => (
                <DateSelect
                  applyButtonText="OK"
                  cancelButtonText="Cancel"
                  label="Suspension End Date"
                  useModal={true}
                  onChange={onChange}
                  supportingText="Leave blank if indefinite"
                  useSingleDate
                  value={value}
                />
              )}
            />
          </div>
        )}
        <FileUpload
          accept=".pdf, .docx, .xlsx, .jpg, .png"
          subtitle="Allowed file extensions: pdf, docx, xlsx, jpg, png"
          handleUpload={(file) => {
            setSelectedFile(file);
            setFileUploadErrorMsg("");
          }}
          errorMsg={fileUploadErrorMsg}
        />
      </div>
    </Modal>
  );
}
